import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import Button from "@material-ui/core/Button";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as Yup from "yup";

import AuthFrame from "@components/AuthFrame";
import AuthenticationService from "@services/Authentication";
import { notifyTimeout, notifyDisabledUser } from "@store/modules/auth";
import AuthTemplate from "@templates/Auth";
import { confirmationPasswordRule } from "@src/utils/validation";

const useStyles = makeStyles((theme) => ({
  paper: {
    maxWidth: "100%",
    "@media all and (min-width: 768px)": {
      width: "320px",
    },
  },
  avatar: {
    margin: theme.spacing(1),
    backgroundColor: "#660099",
  },
  form: {
    width: "100%", // Fix IE 11 issue.
    marginTop: theme.spacing(1),
  },
  submit: {
    margin: theme.spacing(2, 0, 0),
  },
  login: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    marginTop: theme.spacing(2),
  },
  loginLink: {
    color: "#3f51b5!important",
    cursor: "pointer",
    textDecoration: "none!important",
  },
}));

function Login(props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();

  const [loading, setLoading] = useState(false);

  let emailInputRef = null;

  let passwordConfirmationInputRef = null;

  let codeInputRef = null;

  function handleSubmit(values) {
    if (loading) {
      return;
    }

    setLoading(true);

    AuthenticationService.confirmForgotPassword(
      values.login,
      values.code,
      values.password
    )
      .then(() => {
        toast.success("Senha alterada com sucesso!");
        history.push("/");
        setLoading(false);
      })
      .catch(() => {
        toast.error("Falha ao alterar senha!");
        setLoading(false);
      });
  }

  const timeoutNotification = useSelector(
    (state) => state.auth.timeoutNotification
  );
  const disabledUserNotification = useSelector(
    (state) => state.auth.disabledUserNotification
  );

  useEffect(() => {
    function showSessionExpiredAlert() {
      toast.warn("Tempo de sessão expirado!");
      dispatch(notifyTimeout(false));
    }

    function showDisabledUserAlert() {
      toast.warn("Acesso negado!");
      dispatch(notifyDisabledUser(false));
    }

    if (timeoutNotification) {
      showSessionExpiredAlert();
    }

    if (disabledUserNotification) {
      showDisabledUserAlert();
    }
  }, [timeoutNotification, disabledUserNotification, dispatch]);

  const initialValues = {
    login: "",
    code: "",
    password: "",
    passwordConfirmation: "",
  };
  const validationSchema = Yup.object().shape({
    login: Yup.string().required("Campo obrigatório"),
    code: Yup.string().required("Campo obrigatório"),
    password: Yup.string().required("Campo obrigatório"),
    passwordConfirmation: confirmationPasswordRule("password"),
  });

  return (
    <AuthTemplate>
      <AuthFrame banner="left">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          isInitialValid={false}
        >
          {({ errors, touched, isValid, values, handleChange, handleBlur }) => (
            <div className={classes.paper}>
              <Typography component="h1" variant="h5">
                Redefinir Senha
              </Typography>
              {/* login input */}
              <TextField
                value={values.login}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.login ? errors.login : ""}
                error={touched.login && Boolean(errors.login)}
                variant="outlined"
                margin="normal"
                fullWidth
                required
                id="login"
                label="Usuário"
                name="login"
                autoComplete="login"
                onKeyPress={(e) => {
                  if (e.key === "Enter" && emailInputRef) {
                    emailInputRef.focus();
                  }
                }}
              />
              {/* end login input */}

              {/* code input */}
              <TextField
                value={values.code}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.code ? errors.code : ""}
                error={touched.code && Boolean(errors.code)}
                variant="outlined"
                margin="normal"
                fullWidth
                required
                id="code"
                label="Código"
                name="code"
                autoComplete="code"
                onKeyPress={(e) => {
                  if (e.key === "Enter" && codeInputRef) {
                    codeInputRef.focus();
                  }
                }}
              />
              {/* end code input */}

              {/* password input */}
              <TextField
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={touched.password ? errors.password : ""}
                error={touched.password && Boolean(errors.password)}
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="password"
                label="Senha"
                type="password"
                id="password"
                onKeyPress={(e) => {
                  if (e.key === "Enter" && passwordConfirmationInputRef) {
                    passwordConfirmationInputRef.focus();
                  }
                }}
              />
              {/* end password input */}
              {/* password confirmation input */}
              <TextField
                value={values.passwordConfirmation}
                onChange={handleChange}
                onBlur={handleBlur}
                helperText={
                  touched.passwordConfirmation
                    ? errors.passwordConfirmation
                    : ""
                }
                error={
                  touched.passwordConfirmation &&
                  Boolean(errors.passwordConfirmation)
                }
                variant="outlined"
                margin="normal"
                required
                fullWidth
                name="passwordConfirmation"
                label="Redigitação da Senha"
                type="password"
                id="passwordConfirmation"
                inputRef={(element) => {
                  passwordConfirmationInputRef = element;
                }}
                onKeyPress={(e) => {
                  if (e.key === "Enter" && isValid && !loading) {
                    handleSubmit(values);
                  }
                }}
              />
              {/* end password confirmation input */}
              {/* end repeatedPassword input */}

              {/* submit button */}
              <Button
                onClick={() => {
                  handleSubmit(values);
                }}
                fullWidth
                variant="contained"
                color="primary"
                size="large"
                className={classes.submit}
                disabled={!isValid || loading}
              >
                Redefinir
              </Button>
              {/* end submit button */}
            </div>
          )}
        </Formik>
        <div className={classes.login}>
          <Link to="/login" className={classes.loginLink}>
            <Typography>Login</Typography>
          </Link>
        </div>
      </AuthFrame>
    </AuthTemplate>
  );
}

export default Login;
